import React from 'react';
import { 
  Cpu, 
  Zap, 
  Coins, 
  Activity, 
  Layers, 
  TrendingUp, 
  AlertTriangle, 
  CheckCircle2,
  Users
} from 'lucide-react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell, PieChart, Pie } from 'recharts';

export default function AdminAiTelemetry({ aiStats }) {
  if (!aiStats) return null;

  const {
    totalCostEur = 0,
    totalTokens = 0,
    promptTokens = 0,
    completionTokens = 0,
    totalRequests = 0,
    budgetLimitEur = 25.0,
    breakdownByType = [],
    breakdownByModel = [],
    topConsumers = []
  } = aiStats;

  const budgetPercent = Math.min(100, Math.round((totalCostEur / budgetLimitEur) * 100));
  const avgCostPerReq = totalRequests > 0 ? (totalCostEur / totalRequests) : 0;
  const avgTokensPerReq = totalRequests > 0 ? Math.round(totalTokens / totalRequests) : 0;

  const COLORS = ['#10b981', '#00e5ff', '#8b5cf6', '#f59e0b', '#ec4899', '#3b82f6'];

  const formatPromptTypeName = (name) => {
    if (!name) return 'General';
    const map = {
      'morning_briefing': 'Morning Brief',
      'morning_brief': 'Morning Brief',
      'strava_activity': 'Análisis Strava',
      'strava_activity_analysis': 'Análisis Strava',
      'ai_coach_chat': 'Coach IA Chat',
      'ai_coach': 'Coach IA',
      'ai_weekly_report': 'Informe Semanal',
      'ai_weekly': 'Informe Semanal',
      'medical_report': 'Informe Médico',
      'workout_generation': 'Prescripción Entreno'
    };
    return map[name] || name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', marginBottom: '2rem' }}>
      
      {/* HEADER TELEMETRÍA IA */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.8rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: 'rgba(139, 92, 246, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#a78bfa' }}>
            <Cpu size={20} />
          </div>
          <div>
            <h3 style={{ margin: 0, fontSize: '1.25rem', color: 'white', fontWeight: 700 }}>Observabilidad & Salud del Motor IA</h3>
            <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.85rem' }}>Telemetría de consumo, distribución de costes y latencia del sistema</p>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'rgba(16, 185, 129, 0.1)', border: '1px solid rgba(16, 185, 129, 0.25)', padding: '0.4rem 0.8rem', borderRadius: '20px' }}>
          <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#10b981', boxShadow: '0 0 8px #10b981' }} />
          <span style={{ color: '#10b981', fontSize: '0.8rem', fontWeight: 600 }}>Gemini 2.5 Nominal</span>
        </div>
      </div>

      {/* TOP ROW: CONTROL DE PRESUPUESTO + KPIS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '1rem' }}>
        
        {/* Presupuesto y Soft Limit */}
        <div className="glass-panel" style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', background: 'linear-gradient(145deg, rgba(30, 27, 75, 0.5) 0%, rgba(17, 24, 39, 0.8) 100%)', border: '1px solid rgba(139, 92, 246, 0.25)' }}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase' }}>Presupuesto IA (Mes)</span>
              <span style={{ color: budgetPercent > 80 ? '#ef4444' : '#10b981', fontSize: '0.8rem', fontWeight: 700 }}>{budgetPercent}% usado</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginBottom: '0.8rem' }}>
              <span style={{ fontSize: '1.8rem', fontWeight: 800, color: 'white' }}>€{totalCostEur.toFixed(3)}</span>
              <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>/ €{budgetLimitEur.toFixed(2)} límite</span>
            </div>
          </div>
          <div>
            <div style={{ width: '100%', height: '8px', background: 'rgba(255,255,255,0.08)', borderRadius: '4px', overflow: 'hidden' }}>
              <div 
                style={{ 
                  width: `${budgetPercent}%`, 
                  height: '100%', 
                  background: budgetPercent > 80 ? 'linear-gradient(90deg, #f59e0b, #ef4444)' : 'linear-gradient(90deg, #00e5ff, #8b5cf6)',
                  borderRadius: '4px',
                  transition: 'width 0.8s ease'
                }} 
              />
            </div>
            <p style={{ margin: '0.5rem 0 0 0', color: 'var(--text-secondary)', fontSize: '0.75rem' }}>
              {budgetPercent < 50 ? 'Margen operativo óptimo' : budgetPercent < 80 ? 'Consumo moderado' : 'Alerta: cerca del límite'}
            </p>
          </div>
        </div>

        {/* Total Tokens Desglosados */}
        <div className="glass-panel" style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase' }}>Volumen de Tokens</span>
            <Layers size={16} color="var(--health-cyan)" />
          </div>
          <div style={{ fontSize: '1.8rem', fontWeight: 800, color: 'white', marginBottom: '0.4rem' }}>
            {totalTokens.toLocaleString('es-ES')}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: 'var(--text-muted)', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '0.5rem' }}>
            <span>Prompt: <strong style={{ color: 'white' }}>{promptTokens.toLocaleString('es-ES')}</strong></span>
            <span>Completion: <strong style={{ color: 'white' }}>{completionTokens.toLocaleString('es-ES')}</strong></span>
          </div>
        </div>

        {/* Coste y Tokens Medios por Petición */}
        <div className="glass-panel" style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', fontWeight: 600, textTransform: 'uppercase' }}>Eficiencia por Petición</span>
            <Coins size={16} color="#f59e0b" />
          </div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.4rem', marginBottom: '0.4rem' }}>
            <span style={{ fontSize: '1.8rem', fontWeight: 800, color: '#00e5ff' }}>€{avgCostPerReq.toFixed(4)}</span>
            <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>/ req</span>
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '0.5rem', display: 'flex', justifyContent: 'space-between' }}>
            <span>Promedio: <strong style={{ color: 'white' }}>{avgTokensPerReq}</strong> tokens/req</span>
            <span style={{ color: '#10b981', fontWeight: 600 }}>{totalRequests} peticiones</span>
          </div>
        </div>

      </div>

      {/* MID ROW: DISTRIBUCIÓN POR TIPO + TOP CONSUMIDORES */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)', gap: '1.5rem' }}>
        
        {/* Gráfica de Peticiones y Costes por Función IA */}
        <div className="glass-panel" style={{ padding: '1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
            <div>
              <h4 style={{ margin: 0, color: 'white', fontSize: '1rem', fontWeight: 600 }}>Carga por Funcionalidad IA</h4>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.8rem' }}>Peticiones y gasto generado por cada módulo</p>
            </div>
            <span style={{ fontSize: '0.75rem', color: 'var(--health-cyan)', background: 'rgba(0, 229, 255, 0.1)', padding: '0.2rem 0.6rem', borderRadius: '10px' }}>
              {breakdownByType.length} módulos activos
            </span>
          </div>

          {breakdownByType.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>Sin actividad de IA registrada</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem' }}>
              {breakdownByType.map((item, idx) => {
                const percent = totalRequests > 0 ? Math.round((item.count / totalRequests) * 100) : 0;
                const color = COLORS[idx % COLORS.length];
                return (
                  <div key={item.type || idx} style={{ background: 'rgba(255,255,255,0.02)', padding: '0.75rem 1rem', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.04)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{ width: '10px', height: '10px', borderRadius: '3px', background: color }} />
                        <span style={{ color: 'white', fontWeight: 600, fontSize: '0.85rem' }}>{formatPromptTypeName(item.type)}</span>
                      </div>
                      <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', fontSize: '0.85rem' }}>
                        <span style={{ color: 'var(--text-muted)' }}>{item.count} reqs ({percent}%)</span>
                        <span style={{ color: '#00e5ff', fontWeight: 700 }}>€{(item.costEur || 0).toFixed(3)}</span>
                      </div>
                    </div>
                    <div style={{ width: '100%', height: '4px', background: 'rgba(255,255,255,0.06)', borderRadius: '2px', overflow: 'hidden' }}>
                      <div style={{ width: `${percent}%`, height: '100%', background: color, borderRadius: '2px' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top Consumidores de IA */}
        <div className="glass-panel" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.2rem' }}>
            <div>
              <h4 style={{ margin: 0, color: 'white', fontSize: '1rem', fontWeight: 600 }}>Top Consumidores de IA</h4>
              <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.8rem' }}>Usuarios con mayor demanda de inferencia</p>
            </div>
            <Users size={18} color="var(--text-muted)" />
          </div>

          {topConsumers.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', margin: 'auto' }}>
              Sin registros de usuarios aún
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', flex: 1 }}>
              {topConsumers.map((consumer, idx) => (
                <div 
                  key={consumer.userId || idx} 
                  style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '0.75rem 1rem',
                    background: 'rgba(255,255,255,0.03)',
                    borderRadius: '10px',
                    border: '1px solid rgba(255,255,255,0.05)'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                    <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'rgba(139, 92, 246, 0.2)', color: '#c4b5fd', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.8rem' }}>
                      {idx + 1}
                    </div>
                    <div>
                      <div style={{ color: 'white', fontWeight: 600, fontSize: '0.85rem' }}>{consumer.name}</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem' }}>{consumer.email} · <span style={{ color: consumer.role === 'trainer' ? 'var(--health-cyan)' : 'var(--health-green)' }}>{consumer.role === 'trainer' ? 'Entrenador' : 'Atleta'}</span></div>
                    </div>
                  </div>

                  <div style={{ textAlign: 'right' }}>
                    <div style={{ color: 'var(--strava-orange)', fontWeight: 800, fontSize: '0.95rem' }}>€{(consumer.costEur || 0).toFixed(3)}</div>
                    <div style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>{consumer.requests} reqs</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>

    </div>
  );
}
